interface DateRangeBounds {
  start: Date | null | undefined
  end: Date | null | undefined
}

/**
 * Standalone wording stands on its own in a toolbar control; connector wording
 * reads after a chip's "is" connector ("Created is from Mar 14").
 */
type DateRangeLabelStyle = "standalone" | "connector"

interface DateRangeLabelOptions {
  style?: DateRangeLabelStyle
  now?: Date
}

const WORDING: Record<
  DateRangeLabelStyle,
  { separator: string; from: string; until: string }
> = {
  standalone: { separator: " - ", from: "From", until: "Until" },
  connector: { separator: " – ", from: "from", until: "until" },
}

function isUsableDate(date: Date | null | undefined): date is Date {
  return date instanceof Date && !Number.isNaN(date.getTime())
}

function formatBoundary(date: Date, currentYear: number): string {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === currentYear ? {} : { year: "numeric" }),
  })
}

/**
 * Human label for an applied date range, or null when no bound is set. Toolbar
 * date controls and filter chips both carry it, so the applied range is
 * readable without opening the popover.
 *
 * A boundary outside the current year carries its year, so a range over older
 * data does not read as a recent one.
 */
export function formatDateRangeLabel(
  dateRange: DateRangeBounds | null | undefined,
  { style = "standalone", now = new Date() }: DateRangeLabelOptions = {},
): string | null {
  const start = isUsableDate(dateRange?.start) ? dateRange.start : null
  const end = isUsableDate(dateRange?.end) ? dateRange.end : null
  const currentYear = now.getFullYear()
  const wording = WORDING[style]

  if (start && end) {
    return `${formatBoundary(start, currentYear)}${wording.separator}${formatBoundary(end, currentYear)}`
  }
  if (start) {
    return `${wording.from} ${formatBoundary(start, currentYear)}`
  }
  if (end) {
    return `${wording.until} ${formatBoundary(end, currentYear)}`
  }
  return null
}
